import { lazy, Suspense, useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams, useParams } from "react-router-dom";
import api from "@/lib/api";
import ProfessionalCard from "@/components/ProfessionalCard";
import SearchBar from "@/components/SearchBar";
import ExploreLocations from "@/components/ExploreLocations";
import SEO from "@/components/SEO";
import { unslugify } from "@/lib/geo";
import { sanitizeLocationValue } from "@/lib/locationSanitizers";

import {
  ArrowRight,
  Navigation,
  Loader2,
  Flame,
  ShieldCheck,
  X,
} from "lucide-react";
import FilterField from "@/components/ui/FilterField";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { CategoryIcon } from "@/lib/categoryIcon";
import { toast } from "sonner";
import { slugify, haversineKm } from "@/lib/geo";
import { buildListingPath } from "@/lib/listings";
import { loadUserPrefs, storeUserPrefs } from "@/lib/preferences";
import { useMobileOverlay } from "@/contexts/MobileOverlayContext";
import useUserLocation from "@/hooks/useUserLocation";
import useSearchFilters, {
  DISTANCE_OPTIONS,
  enrichListings,
  buildApiParams,
} from "@/hooks/useSearchFilters";

const LocationMap = lazy(() => import("@/components/LocationMap"));

interface VerifiedStatsBarProps {
  verifiedCount: number | null;
  avatars: string[];
  totalViews?: number | null;
}

function VerifiedStatsBar({ stats }: { stats: VerifiedStatsBarProps }) {
  const { verifiedCount, avatars, totalViews } = stats;
  const count = verifiedCount ?? 0;
  const views = totalViews ?? 0;
  return (
    <div
      className="mb-8 flex flex-wrap items-center gap-5"
      data-testid="verified-stats"
    >
      {avatars.length > 0 && (
        <div className="flex -space-x-2">
          {avatars.slice(0, 4).map((src, i) => (
            <img
              key={`${src}-${i}`}
              src={src}
              loading="lazy"
              decoding="async"
              className="w-9 h-9 rounded-full border-[3px] border-cream-50 object-cover shadow-sm bg-cream-100"
              alt="Acompanhante verificado"
            />
          ))}
          {count > 4 && (
            <div className="w-9 h-9 rounded-full border-[3px] border-cream-50 bg-heat-grad flex items-center justify-center text-[10px] font-black text-white shadow-sm">
              +
              {count - 4 > 999
                ? `${Math.floor((count - 4) / 1000)}k`
                : count - 4}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 text-sm text-ink-700">
        <ShieldCheck className="w-4 h-4 text-emerald-500" />
        <span>
          <span className="font-bold text-ink-900">
            {count > 999
              ? `${Math.floor(count / 1000)}k`
              : count.toLocaleString("pt-BR")}
          </span>{" "}
          anúncios ativos e verificados
        </span>
      </div>
      {views > 0 && (
        <div className="text-sm text-ink-500">
          {views.toLocaleString("pt-BR")} visualiza{views === 1 ? "ção" : "ções"}
        </div>
      )}
    </div>
  );
}

export { VerifiedStatsBar };
export default function Home() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [searchParams] = useSearchParams();
  const { stateSlug, citySlug } = useParams<{ stateSlug: string; citySlug: string }>();
  const stateFromPath = sanitizeLocationValue(stateSlug || "");
  const cityFromPath = stateFromPath && citySlug ? sanitizeLocationValue(unslugify(citySlug)) : "";
  const [categories, setCategories] = useState<Array<{ name: string; slug: string; icon?: string }>>([]);
  const [subcategories, setSubcategories] = useState<Array<{ name: string; slug: string }>>([]);
  const [subcategoriesLoadedFor, setSubcategoriesLoadedFor] = useState<string | null>(null);
  const [listings, setListings] = useState<Array<Record<string, unknown>>>([]);
  const [attributeCategories, setAttributeCategories] = useState<Array<{ id: string; name: string; options: Array<{ label: string; value: string }> }>>([]);
  const [loading, setLoading] = useState(true);
  const [serviceOptions, setServiceOptions] = useState<Array<{ id: string; name: string }>>([]);

  const initialPrefs = loadUserPrefs();
  const {
    filters,
    setCategory,
    setSubcategory,
    setState,
    setCity,
    setNeighborhood,
    setQ,
    setMaxKm,
    setSort: setSortMode,
    setOnline: setOnlineOnly,
    setService: setSelectedService,
    setAttributes: hookSetAttributes,
    clearFilters: hookClearFilters,
  } = useSearchFilters({
    initialFilters: {
      category: searchParams.get("category") || initialPrefs?.category || "",
      subcategory:
        searchParams.get("subcategory") || initialPrefs?.subcategory || "",
      state: stateFromPath || searchParams.get("state") || initialPrefs?.state || "",
      city: cityFromPath || searchParams.get("city") || initialPrefs?.city || "",
      neighborhood:
        searchParams.get("neighborhood") || initialPrefs?.neighborhood || "",
      q: searchParams.get("q") || initialPrefs?.q || "",
      attributes: initialPrefs?.attributes || {},
      maxKm: initialPrefs?.maxKm ?? null,
      online: false,
      service: [],
      sort: "default",
    },
  });

  // Sincroniza filtros quando a URL muda via SEO paths /:stateSlug e /:stateSlug/:citySlug.
  // useSearchFilters só usa initialFilters no mount, então precisamos propagar
  // navegações internas (ex: clicar em cidade/estado) para o estado de filtros.
  // IMPORTANTE: só atualiza quando a URL TEM o parâmetro — não limpa filtros
  // quando a URL não tem (preserva userPrefs).
  useEffect(() => {
    const nextState = stateFromPath || searchParams.get("state");
    const nextCity = cityFromPath || searchParams.get("city");
    if (nextState != null && nextState !== filters.state) setState(nextState);
    if (nextCity != null && nextCity !== filters.city) setCity(nextCity);
    const qpCat = searchParams.get("category");
    if (qpCat != null && qpCat !== filters.category) setCategory(qpCat);
    const qpSub = searchParams.get("subcategory");
    if (qpSub != null && qpSub !== filters.subcategory) setSubcategory(qpSub);
    const qpQ = searchParams.get("q");
    if (qpQ != null && qpQ !== filters.q) setQ(qpQ);
    // Filtros de atributo vindos da URL (attr_<id>=v1,v2). Só atualiza quando
    // difere do estado atual — evita loop com o drawer de filtros mobile.
    const urlAttrs: Record<string, string[]> = {};
    let attrsChanged = false;
    searchParams.forEach((val, key) => {
      if (key.startsWith("attr_")) {
        const id = key.slice(5);
        urlAttrs[id] = val.split(",").map((s) => s.trim()).filter(Boolean);
        attrsChanged = true;
      }
    });
    if (attrsChanged) {
      const current = filters.attributes as Record<string, string[]>;
      const same =
        Object.keys(urlAttrs).length === Object.keys(current).length &&
        Object.entries(urlAttrs).every(
          ([id, vals]) =>
            JSON.stringify(current[id]) === JSON.stringify(vals),
        );
      if (!same) {
        Object.entries(urlAttrs).forEach(([id, vals]) => {
          hookSetAttributes(id, vals);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFromPath, cityFromPath, searchParams]);

  const category = filters.category;
  const subcategory = filters.subcategory;
  const state = filters.state;
  const city = filters.city;
  const neighborhood = filters.neighborhood;
  const q = filters.q;
  const maxKm = filters.maxKm;
  const sortMode = filters.sort;
  const onlineOnly = filters.online;
  const selectedAttributes = filters.attributes;
  const selectedService = filters.service;

  const setSelectedAttributes = (updater: Record<string, string[]> | ((prev: Record<string, string[]>) => Record<string, string[]>)) => {
    const next = typeof updater === "function" ? updater(filters.attributes as Record<string, string[]>) : updater;
    Object.entries(next).forEach(([id, vals]) => {
      hookSetAttributes(id, vals as string[]);
    });
  };

  // Reutilizado tanto no SearchBar do hero quanto no drawer mobile.
  const onCategoryChange = (next: string) => {
    setCategory(next);
    setSubcategory("");
    storeUserPrefs({ category: next });
  };
  const [publicStats, setPublicStats] = useState({
    verifiedCount: null,
    avatars: [],
    totalListings: null,
    activeDaysTotal: null,
    boostsCount: null,
    highlightsCount: null,
    citiesCount: null,
    totalViews: null,
  });
  const {
    userLocation,
    loading: geoLoading,
    error: geoError,
    requestLocation,
    clear: clearMyLocation,
  } = useUserLocation();

  const filterLocation = useMemo(() => ({ state, city, neighborhood }), [state, city, neighborhood]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/attributes").then((r) => setAttributeCategories(r.data));
    api
      .get("/stats/public", { params: { avatar_limit: 5 } })
      .then((r) =>
        setPublicStats({
          verifiedCount: r.data.verified_listings_count ?? r.data.verified_count ?? null,
          avatars: r.data.avatars || [],
          totalListings: r.data.total_listings ?? null,
          activeDaysTotal: r.data.active_days_total ?? null,
          boostsCount: r.data.boosts_count ?? null,
          highlightsCount: r.data.highlights_count ?? null,
          citiesCount: r.data.cities_count ?? null,
          totalViews: r.data.total_views ?? null,
        }),
      )
      .catch(() => {});
    api
      .get("/options/services")
      .then((r) => setServiceOptions(r.data || []))
      .catch(() => setServiceOptions([]));
  }, []);

  // Recarrega subcategorias sempre que a categoria muda. Sem categoria,
  // não faz sentido oferecer subcategoria.
  useEffect(() => {
    if (!category) {
      setSubcategories([]);
      setSubcategoriesLoadedFor("");
      return;
    }
    const target = category;
    setSubcategoriesLoadedFor(null);
    let cancelled = false;
    api
      .get("/subcategories", { params: { category: target } })
      .then((r) => {
        if (cancelled) return;
        setSubcategories(r.data || []);
        setSubcategoriesLoadedFor(target);
      })
      .catch(() => {
        if (cancelled) return;
        setSubcategories([]);
        setSubcategoriesLoadedFor(target);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  // Quando a lista de subcategorias chega, valida a subcat atual: se não
  // pertence à categoria atual, limpa (evita filtro fantasma). Só age
  // quando a lista carregada corresponde à categoria atual — assim, abrir
  // /?category=X&subcategory=Y na URL não derruba Y antes do GET chegar.
  useEffect(() => {
    if (subcategoriesLoadedFor !== category) return;
    if (!subcategory) return;
    const ok = subcategories.some((s) => s.slug === subcategory);
    if (!ok) setSubcategory("");
  }, [subcategories, subcategory, subcategoriesLoadedFor, category, setSubcategory]);

  // Persiste TODOS os filtros da busca em localStorage. Assim qualquer
  // alteração na Home reaparece automaticamente em qualquer rota SEO
  // (/:stateSlug, /:stateSlug/:citySlug), atendendo o requisito de "dados da pesquisa persistem
  // globalmente".
  useEffect(() => {
    storeUserPrefs({
      category,
      subcategory,
      state,
      city,
      neighborhood,
      q,
      attributes: selectedAttributes,
    });
  }, [category, subcategory, state, city, neighborhood, q, selectedAttributes]);

  const hasActiveFilters = !!(
    category ||
    subcategory ||
    state ||
    city ||
    neighborhood ||
    q ||
    Object.values(selectedAttributes).some((v) => v.length > 0)
  );

  const hasAnyFilter =
    hasActiveFilters || selectedService.length > 0 || !!userLocation;

  // Alias usado pelo drawer mobile (e pelo SearchBar do hero).
  const onSubcategoryChange = setSubcategory;

  const onSearch = useCallback((e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    storeUserPrefs({ category, state, city, neighborhood });
    let path = "/";
    if (state) {
      path = `/${state.toLowerCase()}`;
      if (city) path += `/${slugify(city)}`;
    }
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (subcategory) qs.set("subcategory", subcategory);
    if (selectedService.length) qs.set("service", selectedService.join(","));
    if (neighborhood) qs.set("neighborhood", neighborhood);
    if (q && q.trim()) qs.set("q", q.trim());

    // Add attributes to URL
    Object.entries(selectedAttributes).forEach(([id, vals]) => {
      if (vals && vals.length > 0) {
        qs.set(`attr_${id}`, vals.join(","));
      }
    });

    const query = qs.toString();
    navigate(`${path}${query ? `?${query}` : ""}`);
  }, [category, subcategory, state, city, neighborhood, q, selectedService, selectedAttributes, navigate]);

  const onApply = useCallback(() => {
    onSearch(undefined);
  }, [onSearch]);

  const {
    setFilterProps: ctxSetFilterProps,
    setMapProps: ctxSetMapProps,
  } = useMobileOverlay();

  // Publica os filtros para o drawer mobile (renderizado fora das Routes).
  useEffect(() => {
    ctxSetFilterProps({
      q,
      onQChange: setQ,
      category,
      categories,
      onCategoryChange,
      subcategory,
      subcategories,
      onSubcategoryChange,
      location: filterLocation,
      onLocationChange,
      attributeCategories,
      selectedAttributes,
      setSelectedAttributes,
      serviceOptions,
      selectedService,
      setSelectedService,
      onlineOnly,
      setOnlineOnly,
      sortMode,
      setSortMode,
      maxKm,
      setMaxKm,
      userLocation,
      clearFilters,
      clearUserLocation: clearMyLocation,
      hasActiveFilters,
      onApply,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    q, category, subcategory, state, city, neighborhood, selectedAttributes,
    onlineOnly, sortMode, maxKm, userLocation, hasActiveFilters, categories,
    subcategories, attributeCategories, serviceOptions, onApply, clearMyLocation,
  ]);

  // Publica a lista + localização para a faixa de mapa mobile.
  useEffect(() => {
    ctxSetMapProps({
      listings,
      userLocation,
      maxKm,
      filterState: state,
      filterCity: city,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, userLocation, maxKm, state, city]);

  useEffect(() => {
    if (hasAnyFilter === false) {
      setListings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = buildApiParams(filters, userLocation, {
      includeAttributes: true,
      includeSort: false,
      limit: 100,
    });

    api
      .get("/listings", { params })
      .then((r) => {
        setListings(r.data.items);
      })
      .finally(() => setLoading(false));
  }, [
    filters,
    userLocation,
    hasAnyFilter,
  ]);

  const listingsEnriched = useMemo(() => {
    return enrichListings(listings, categories, userLocation, filters);
  }, [listings, categories, userLocation, filters]);

  const visibleListings = listingsEnriched;

  const mapMarkers = useMemo(() => {
    if (!userLocation) return [];
    return listingsEnriched
      .filter((l) => l.lat != null && l.lng != null)
      .slice(0, 60)
      .map((l) => {
        const km = haversineKm(userLocation, { lat: l.lat as number, lng: l.lng as number });
        const distanceLabel =
          km == null
            ? null
            : km < 1
              ? `${Math.round(km * 1000)} m`
              : `${km.toFixed(1)} km`;
        return {
          id: l.id as string,
          lat: l.lat as number,
          lng: l.lng as number,
          name: l.name,
          photo: l.cover_photo || (l.photos && l.photos[0]) || null,
          href: buildListingPath(l) || `/acompanhante/${l.id}`,
          distanceLabel,
        };
      });
  }, [listingsEnriched, userLocation]);

  const onLocationChange = (next: { state?: string; city?: string; neighborhood?: string }): void => {
    setState(next.state || "");
    setCity(next.city || "");
    setNeighborhood(next.neighborhood || "");
    storeUserPrefs({
      state: next.state,
      city: next.city,
      neighborhood: next.neighborhood,
    });
  };

  const handleLocate = async () => {
    try {
      const { match } = await requestLocation({ withGeocode: true });

      if (match?.state) {
        setState(match.state);
        setCity(match.city || "");
        // Não preenche bairro automaticamente
        storeUserPrefs({
          state: match.state,
          city: match.city || "",
          neighborhood: "",
        });

        const detected = [match.city, match.state].filter(Boolean).join(", ");
        if (detected) toast.success(`Mostrando acompanhantes em ${detected}.`);

        // Stay on Home page - just update local state, don't navigate
        // The listings will auto-refresh via userLocation dependency
      } else {
        toast.message(
          "Não localizamos sua região no nosso catálogo. Mostrando todos os acompanhantes.",
        );
        // Don't navigate - just clear location filters
        setState("");
        setCity("");
        setNeighborhood("");
      }
    } catch (e: unknown) {
      const code = (e as { code?: number })?.code;
      const msg =
        code === 1
          ? "Permissão de localização negada. Habilite no navegador para encontrar acompanhantes perto de você."
          : code === 3
            ? "Tempo esgotado ao obter sua localização. Tente novamente."
            : code === 0
              ? "Localização indisponível no momento. Verifique os serviços de localização e tente novamente."
              : (e as { message?: string })?.message || "Não foi possível obter sua localização.";
      toast.error(msg);
    }
  };

  const handleClearLocation = () => {
    clearMyLocation();
    setMaxKm(null);
  };

  const clearFilters = () => {
    hookClearFilters();
    setSelectedService([]);
    setSelectedAttributes({});
    // Preserve context filters (category, state, city) so "Limpar filtros"
    // doesn't wipe the user's browsing context — they persist across pages
    // and only change when the user picks a different value manually or uses
    // the location flow.
    storeUserPrefs({
      category: filters.category || "",
      subcategory: "",
      state: filters.state || "",
      city: filters.city || "",
      neighborhood: "",
      q: "",
      attributes: {},
    });
    navigate("/", { replace: true });
  };

  return (
    <div className="bg-cream-50">
      <SEO title={undefined} path={routerLocation.pathname} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Warm radial glow background */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(85% 100% at 0% 0%, rgba(225,29,72,0.35), transparent 70%), radial-gradient(75% 85% at 100% 100%, rgba(236,72,153,0.30), transparent 75%), radial-gradient(50% 60% at 50% 40%, rgba(251,113,133,0.15), transparent 65%), linear-gradient(160deg, rgb(var(--cream-50)) 0%, rgb(var(--cream-100)) 70%, rgba(225,29,72,0.08) 100%)",
          }}
        />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="container-custom relative pt-4 pb-6 lg:pb-8">
          <div className="w-full">
            <div
              className="flex flex-wrap items-center gap-2 mb-6 reveal"
              data-testid="hero-stats"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-cream-200 backdrop-blur-sm">
                <Flame className="w-3.5 h-3.5 text-heat-500 fill-heat-500" />
                <span className="text-xs font-semibold text-ink-700 tracking-wide uppercase">
                  Sempre tem
                </span>
              </div>
            </div>

            <h1 className="h-mega text-4xl sm:text-6xl lg:text-7xl text-ink-900 leading-[1.02] mb-6">
              Encontre quem você{" "}
              <em className="not-italic text-heat-grad">deseja</em>
              <span className="text-heat-500">.</span>
              <br className="hidden sm:block" />
              <span className="font-light italic text-ink-600">
                Aqui, perto.
              </span>
            </h1>

            <p className="text-lg text-ink-700 max-w-2xl leading-relaxed mb-10 reveal reveal-delay-2">
              Nossos usuários estão prontos para te atender — realizamos
              verificações periódicas de identidade para garantir a melhor
              experiência. Tudo em um lugar para você, direto, sem
              intermediário, sem apps adicionais, com maior quantidade de
              informações para comparar, escolher e voltar sempre.
            </p>

            {/* Search form */}
              <SearchBar
               variant="listing"
               className="reveal reveal-delay-3 transition-shadow duration-300"
               testId="home-search"
               qTestId="home-search-q"
               hideSearchInput
              q={q}
              onQChange={(val) => {
                setQ(val);
                if (val && window.scrollY > 400) {
                  window.scrollTo({ top: 300, behavior: "smooth" });
                }
              }}
              onSubmit={() => onSearch(undefined)}
              placeholder="Pesquisar por nome ou palavra-chave…"
              category={category}
              categories={categories}
              onCategoryChange={onCategoryChange}
              subcategory={subcategory}
              subcategories={subcategories}
              onSubcategoryChange={setSubcategory}
              location={filterLocation}
              onLocationChange={onLocationChange}
               locationContext={{ category, q }}
               attributeFilters={attributeCategories.map((cat) => {
                 const selected = selectedAttributes[cat.id] || [];
                 return (
                   <FilterField
                     key={cat.id}
                     label={cat.name}
                     className="sm:w-auto sm:min-w-[160px]"
                   >
                     <MultiSelect
                       options={cat.options}
                       selected={selected}
                       onChange={(vals) =>
                         setSelectedAttributes((p) => ({ ...p, [cat.id]: vals }))
                       }
                       placeholder="Todos"
                       className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
                     />
                   </FilterField>
                 );
               })}
               actionButton={
                hasActiveFilters || selectedService.length ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ink-900 text-cream-50 border border-heat-500 text-xs font-bold hover:bg-heat-700 transition-colors whitespace-nowrap"
                    data-testid="filter-clear"
                  >
                    <X className="w-4 h-4" /> Limpar
                  </button>
                ) : null
              }
              onlineOnly={onlineOnly}
              onOnlineToggle={() => setOnlineOnly(!onlineOnly)}
              sortMode={sortMode}
              onSortChange={setSortMode}
            />

            {userLocation && (
              <div className="mt-6 rounded-3xl overflow-hidden border border-cream-200 shadow-soft relative z-10 h-48 sm:h-56 w-full">
                <Suspense
                  fallback={
                    <div className="w-full h-full bg-cream-100 animate-pulse" />
                  }
                >
                  <LocationMap
                    value={userLocation}
                    markers={mapMarkers}
                    readOnly
                    maxKm={maxKm}
                  />
                </Suspense>
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-semibold border border-cream-200 text-ink-900 shadow-soft">
                  <Navigation className="w-3 h-3 text-heat-500" />
                  Você
                  {mapMarkers.length > 0 && (
                    <span className="ml-1 text-ink-500">
                      · {mapMarkers.length}{" "}
                      {mapMarkers.length === 1 ? "perfil" : "perfis"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleClearLocation}
                    className="ml-1 -mr-1 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-cream-100 text-ink-500 hover:text-ink-900 transition"
                    aria-label="Esquecer minha localização"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {!userLocation && (
              <div className="mt-8">
                <div className="surface-card p-6 lg:p-8 rounded-[2rem] border-dashed border-2 border-cream-300 bg-white/40 backdrop-blur-sm w-full">
                  <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                    <div className="flex items-center gap-6 text-left">
                      <div className="w-14 h-14 rounded-full bg-heat-grad-soft text-heat-700 flex items-center justify-center shrink-0">
                        <Navigation className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-xl text-ink-900 mb-1">
                          Busca por Proximidade
                        </h3>
                        <p className="text-ink-600 text-sm max-w-xl">
                          Ative sua localização para encontrar acompanhantes
                          mais próximos de você e filtrar por raio de distância.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleLocate}
                      disabled={geoLoading}
                      className="btn-primary !rounded-2xl !px-10 !py-3.5 shadow-heat-glow text-base whitespace-nowrap"
                      data-testid="locate-btn"
                    >
                      {geoLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Navigation className="w-5 h-5" />
                      )}
                      {geoLoading ? "Localizando..." : "Me localize agora"}
                    </button>
                  </div>
                  {geoError && (
                    <p className="text-xs text-heat-600 mt-4 font-medium text-center lg:text-left">
                      {geoError}
                    </p>
                  )}
                </div>
              </div>
            )}

            {userLocation && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-3xl border border-cream-200 bg-cream-100/80 backdrop-blur-sm p-4 shadow-soft w-full">
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-heat-500" />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-heat-700">
                    Raio de Busca
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  {DISTANCE_OPTIONS.map((opt) => {
                    const active = maxKm === opt.km;
                    return (
                      <button
                        key={opt.km}
                        type="button"
                        onClick={() => setMaxKm(active ? null : opt.km)}
                        className={`text-xs font-bold px-4 py-2 rounded-full border transition-all ${
                          active
                            ? "bg-heat-grad text-white border-transparent shadow-heat-glow scale-105"
                            : "bg-white text-ink-700 border-cream-200 hover:border-heat-300 hover:text-heat-700 hover:shadow-sm"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setMaxKm(null)}
                    className={`text-xs font-bold px-4 py-2 rounded-full border transition-all ${
                      maxKm == null
                        ? "bg-heat-grad text-white border-transparent shadow-heat-glow scale-105"
                        : "bg-white text-ink-700 border-cream-200 hover:border-heat-300 hover:text-heat-700 hover:shadow-sm"
                    }`}
                  >
                    Todas
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleClearLocation}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ink-900 text-cream-50 border border-heat-500 text-xs font-bold hover:bg-heat-700 transition-colors whitespace-nowrap"
                  data-testid="clear-location"
                >
                  Esquecer Localização
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Resultados — barra sticky removida, controles movidos para #FILTROS_EXTRAS dentro do SearchBar */}
       <div className="relative container-custom">
         {hasAnyFilter ? (
           <>
           {/* Resultados */}
        <section className="bg-cream-50">
          <div className="container-custom pt-6 pb-6 lg:pb-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <span className="overline mb-3 block">Resultados</span>
                <h2 className="h-section text-3xl sm:text-4xl text-ink-900">
                  Acompanhantes para você
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <FilterField label="Ordenar por" className="sm:min-w-[160px]">
                  <MultiSelect
                    options={[
                      { label: "Destaques", value: "default" },
                      { label: "Recentes", value: "recent" },
                      { label: "Nome A-Z", value: "name" },
                      { label: "Próximos", value: "distance" },
                    ]}
                    selected={sortMode ? [sortMode] : ["default"]}
                    onChange={(vals) => setSortMode(vals[0] || "default")}
                    multiple={false}
                    className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
                  />
                </FilterField>
              </div>
            </div>

            {loading ? (
              <div className="cards-grid">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-[420px] rounded-3xl bg-cream-100 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <>
                {publicStats?.verifiedCount != null &&
                  publicStats.verifiedCount > 0 && (
                    <VerifiedStatsBar stats={publicStats} />
                  )}

                {listings.length > 0 ? (
                  <>
                    {visibleListings.length > 0 ? (
                      <div className="cards-grid">
                        {visibleListings.map((listing, idx) => (
                            <ProfessionalCard
                              key={listing.id}
                              listing={listing}
                              idx={idx}
                              userLocation={userLocation}
                            />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-20 rounded-3xl border-2 border-dashed border-cream-300 bg-white/50">
                        <p className="text-ink-500">
                          Nenhum resultado com estes filtros.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-20 rounded-3xl border-2 border-dashed border-cream-300 bg-white/50">
                    <p className="text-ink-500">
                      Nenhum acompanhante encontrado nesta região.
                    </p>
                      </div>
                    )}
                  </>
                )}
                {listings.length > 0 && (
                  <div className="flex justify-center mt-10">
                    <button
                      onClick={() => navigate("/")}
                      className="btn-outline group"
                    >
                      Ver todos{" "}
                      <ArrowRight
                        className="ml-1 group-hover:translate-x-1 transition-transform"
                        size={16}
                      />
                    </button>
                  </div>
                )}
          </div>
        </section>

          </>
        ) : (
          <ExploreLocations
            state={state}
            city={city}
            onSelect={(next) => {
              setState(next.state || "");
              setCity(next.city || "");
            }}
          />
        )}
      </div>
      {/* fim do wrapper sticky */}

        {/* CATEGORIAS */}
        <section className="py-16 lg:py-24 bg-cream-100 border-t border-cream-200 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(50% 40% at 50% 0%, rgba(225,29,72,0.18), transparent 70%)",
            }}
          />
          <div className="container-custom relative">
            <div className="text-center mb-12 max-w-xl mx-auto">
              <span className="overline mb-3 block">O que você procura?</span>
              <h2 className="h-section text-3xl sm:text-4xl text-ink-900">
                Navegue por{" "}
                <em className="text-heat-grad not-italic">categorias</em>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => navigate(`/?category=${encodeURIComponent(cat.slug)}`)}
                  className="group bg-white border border-cream-200 rounded-2xl p-5 sm:p-6 text-center hover:border-heat-300 hover:-translate-y-1 hover:shadow-heat-glow transition-all"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-heat-grad-soft flex items-center justify-center text-heat-700 group-hover:bg-heat-grad group-hover:text-white transition-all">
                    <CategoryIcon
                      name={cat.icon}
                      className="w-[22px] h-[22px]"
                    />
                  </div>
                  <span className="font-display font-semibold text-sm sm:text-base text-ink-900 leading-tight">
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* SERVIÇOS */}
        <section className="py-16 lg:py-20 bg-cream-50 border-t border-cream-200 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              background:
                "radial-gradient(50% 40% at 50% 100%, rgba(225,29,72,0.15), transparent 70%)",
            }}
          />
          <div className="container-custom relative">
            <div className="text-center mb-10 max-w-xl mx-auto">
              <span className="overline mb-3 block">Encontre por serviço</span>
              <h2 className="h-section text-3xl sm:text-4xl text-ink-900">
                Navegue por{" "}
                <em className="text-heat-grad not-italic">serviços</em>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {serviceOptions.map((service) => {
                const isSelected = selectedService.includes(service.name);
                return (
                  <button
                    key={service.id}
                    onClick={() => {
                      const next = selectedService.includes(service.name)
                        ? selectedService.filter((s) => s !== service.name)
                        : [...selectedService, service.name];
                      setSelectedService(next);
                      if (category || subcategory || q || userLocation) {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className={`group bg-white border rounded-2xl p-4 sm:p-5 text-center transition-all ${
                      isSelected
                        ? "border-heat-500 bg-heat-50 shadow-heat-glow -translate-y-1"
                        : "border-cream-200 hover:border-heat-300 hover:-translate-y-1 hover:shadow-heat-glow"
                    }`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center transition-all ${
                      isSelected
                        ? "bg-heat-grad text-white"
                        : "bg-cream-100 text-heat-600 group-hover:bg-heat-grad group-hover:text-white"
                    }`}>
                      <Flame className="w-5 h-5" />
                    </div>
                    <span className="font-display font-semibold text-sm sm:text-base text-ink-900 leading-tight">
                      {service.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Acompanhante */}
        <section className="relative overflow-hidden bg-ink-900">
          <div
            aria-hidden
            className="absolute inset-0 opacity-90 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 80% at 100% 0%, rgba(225,29,72,0.55), transparent 70%), radial-gradient(50% 60% at 0% 100%, rgba(236,72,153,0.45), transparent 70%)",
            }}
          />
          <div className="absolute inset-0 grain pointer-events-none opacity-50" />
          <div className="container-custom relative z-10 py-16 sm:py-20 lg:py-28">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold text-white tracking-wider uppercase mb-6 backdrop-blur-sm">
                <Flame className="w-3.5 h-3.5 fill-current text-heat-300" />
                Para acompanhantes
              </span>
              <h2 className="h-mega text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[1.02]">
                Você é{" "}
                <em className="not-italic text-heat-grad">acompanhante?</em>
              </h2>
              <p className="text-cream-100/90 text-lg mb-10 leading-relaxed max-w-xl">
                Crie seu perfil em minutos, receba contatos diretos no WhatsApp,
                e cresça com destaques pagos quando quiser. Sem comissão, sem
                intermediário.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/registrar")}
                  className="btn-primary"
                >
                  Começar agora
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/como-funciona")}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold backdrop-blur-sm transition-colors text-sm border border-white/20"
                >
                  Como funciona
                </button>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
}
